import React, { useEffect, useState } from 'react'
import { Row, Col, Card, Statistic, Button, Table, Tag, List, Avatar } from 'antd'
import {
  ReloadOutlined,
  AppstoreOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  PercentageOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { deviceApi } from '@api/devices'
import { useSocketStore } from '@stores/socketStore'
import { formatTime, formatHandshakeId, getStateColor } from '@utils/format'
import type { Device } from '@app-types/device'
import styles from './index.module.css'

const Dashboard: React.FC = () => {
  const { socket } = useSocketStore()
  const [isMobile, setIsMobile] = useState(false)

  // 监听窗口大小变化
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])
  
  const { data: stats, refetch: refetchStats, isLoading: statsLoading } = useQuery({
    queryKey: ['statistics'],
    queryFn: deviceApi.getStatistics,
  })

  const { data: devicesData, refetch: refetchDevices } = useQuery({
    queryKey: ['devices'],
    queryFn: deviceApi.getDevices,
  })

  useEffect(() => {
    if (!socket) return

    socket.on('update_state', () => {
      refetchStats()
      refetchDevices()
    })

    return () => {
      socket.off('update_state')
    }
  }, [socket, refetchStats, refetchDevices])

  const handleRefresh = () => {
    refetchStats()
    refetchDevices()
  }

  const onlineRate = stats 
    ? Math.round((stats.online_devices / Math.max(stats.total_devices, 1)) * 100) 
    : 0

  // 最近活动设备
  const recentDevices = devicesData?.devices
    .filter((d: Device) => d.last_seen)
    .sort((a: Device, b: Device) => new Date(b.last_seen!).getTime() - new Date(a.last_seen!).getTime())
    .slice(0, 5) || []

  // 需要关注的设备
  const alertDevices = devicesData?.devices.filter((device: Device) => {
    const heartbeat = device.heartbeat_data
    if (!heartbeat) return false
    const rssi = parseInt(heartbeat.rssi)
    return !device.is_online || rssi < 10 || heartbeat.dtu_state === 'ERROR'
  }).slice(0, 5) || []

  const recentColumns = [
    {
      title: '状态',
      dataIndex: 'is_online',
      width: isMobile ? 50 : 70,
      render: (isOnline: boolean) => (
        <Tag color={isOnline ? 'success' : 'error'} style={{ fontSize: isMobile ? '10px' : '12px', padding: '0 4px' }}>
          {isOnline ? '在' : '离'}
        </Tag>
      ),
    },
    {
      title: '设备ID',
      dataIndex: 'device_id',
      ellipsis: true,
      width: isMobile ? 140 : 200,
    },
    ...(isMobile ? [] : [
      {
        title: '握手ID',
        width: 100,
        render: (_: any, record: Device) => formatHandshakeId(
          record.config_data?.handshake_id_a,
          record.config_data?.handshake_id_b
        ),
      },
    ]),
    {
      title: '最后在线',
      dataIndex: 'last_seen',
      width: isMobile ? 130 : 180,
      render: (time: string) => formatTime(time),
    },
  ]

  return (
    <div className={styles.dashboard}>
      <div className={styles.header}>
        <h1>设备仪表板</h1>
        <Button 
          icon={<ReloadOutlined />} 
          onClick={handleRefresh}
          loading={statsLoading}
          size={isMobile ? 'small' : 'middle'}
        >
          {isMobile ? '' : '刷新'}
        </Button>
      </div>

      <Row gutter={16} className={styles.statsRow}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="设备总数"
              value={stats?.total_devices || 0}
              prefix={<AppstoreOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="在线设备"
              value={stats?.online_devices || 0}
              valueStyle={{ color: '#3f8600' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="离线设备"
              value={stats?.offline_devices || 0}
              valueStyle={{ color: '#cf1322' }}
              prefix={<CloseCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="在线率"
              value={onlineRate}
              suffix="%"
              prefix={<PercentageOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={16} className={styles.contentRow}>
        <Col xs={24} lg={12}>
          <Card 
            title="最近活动设备" 
            extra={<Button type="link" href="/devices">查看全部</Button>}
          >
            <Table
              dataSource={recentDevices}
              columns={recentColumns}
              rowKey="device_id"
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="需要关注的设备">
            {alertDevices.length === 0 ? (
              <div className={styles.noAlerts}>
                <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a' }} />
                <p>所有设备运行正常</p>
              </div>
            ) : (
              <List
                dataSource={alertDevices}
                renderItem={(device: Device) => {
                  const alerts = []
                  if (!device.is_online) alerts.push('设备离线')
                  const rssi = parseInt(device.heartbeat_data?.rssi || '0')
                  if (rssi < 10) alerts.push(`信号弱 (${rssi})`)
                  if (device.heartbeat_data?.dtu_state === 'ERROR') alerts.push('错误状态')
                  
                  return (
                    <List.Item>
                      <List.Item.Meta
                        avatar={<Avatar icon={<WarningOutlined />} style={{ backgroundColor: '#ff4d4f' }} />}
                        title={device.device_id}
                        description={alerts.join(' · ')}
                      />
                    </List.Item>
                  )
                }}
              />
            )}
          </Card>
        </Col>
      </Row>

      {stats?.devices_by_state && Object.keys(stats.devices_by_state).length > 0 && (
        <Row gutter={16} className={styles.contentRow}>
          <Col xs={24}>
            <Card title="设备状态分布">
              <div className={styles.stateList}>
                {Object.entries(stats.devices_by_state).map(([state, count]) => (
                  <div key={state} className={styles.stateItem}>
                    <span 
                      className={styles.stateIndicator} 
                      style={{ backgroundColor: getStateColor(state) }}
                    />
                    <span className={styles.stateName}>{state}</span>
                    <span className={styles.stateCount}>{count}</span>
                  </div>
                ))}
              </div>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  )
}

export default Dashboard
